import * as React from 'react';
import { Form, Formik } from 'formik';
import { SchoolEvent } from 'Src/models/event';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING, REPORT_PRINT_FORMAT } from 'Src/consts/report';
import { isTeamSportEvent, isIndividualSportEvent } from 'Src/helpers/event/event';
import {
  ReportActions,
  ReportDescription,
  ReportFormatSelector,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../ReportFormUi';
import '../../SchoolEventView.scss';

interface Props {
  event: SchoolEvent;
  activeSchoolId: string;
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
}

interface ToggleOption {
  key: string;
  label: string;
  hint: string;
}

//report 017
export function BasicTeamSheetReportForm({ onCloseClick, onSubmit, event, activeSchoolId }: Props) {
  const { embeddedTeams, id: eventId } = event;

  const embeddedTeamsForActiveSchool = embeddedTeams.filter(team => team.schoolId === activeSchoolId);

  const getSelectOptionsForTeams = () => {
    return embeddedTeamsForActiveSchool.map(team => {
      return {
        text: team.name,
        value: team._id
      };
    });
  };

  const teamOptions = getSelectOptionsForTeams();

  const isEmbeddedTeamsExist = teamOptions.length > 0;

  const firstTeamValue = isEmbeddedTeamsExist ? teamOptions[0].value : undefined;

  const studentDetailsOptions: ToggleOption[] = [
    { key: 'isPrintFullNames', label: 'Show full names', hint: 'Otherwise surnames will be shortened to an initial.' },
    {
      key: 'isPrintPhotoConsentOptOut',
      label: 'Show photo consent opt-out',
      hint: 'Mark students whose photo consent has been withdrawn.'
    }
  ];

  const eventDetailsOptions: ToggleOption[] = [
    { key: 'isPrintDescription', label: 'Show event description', hint: 'Include the event description.' },
    { key: 'isPrintKitNotes', label: 'Show kit notes', hint: 'Include kit information added to the event.' },
    { key: 'isPrintComments', label: 'Show event comments', hint: 'Include internal event comments.' },
    {
      key: 'isPrintTransportDetails',
      label: 'Show transport details',
      hint: 'Include travel and transport information.'
    },
    {
      key: 'isPrintOppositionContactDetails',
      label: 'Show opposition contact details',
      hint: 'Include available opposition contacts.'
    },
    {
      key: 'isPrintMembersOfStaff',
      label: 'Show coach and staff names',
      hint: 'Include staff assigned to the event.'
    },
    { key: 'isPrintEventTimings', label: 'Show event timings', hint: 'Include meet, departure and event times.' }
  ];

  const requestInitial = {
    team: firstTeamValue,
    isPrintFullNames: true,
    isPrintPhotoConsentOptOut: false,
    isPrintDescription: true,
    isPrintKitNotes: true,
    isPrintComments: true,
    isPrintTransportDetails: true,
    isPrintOppositionContactDetails: false,
    isPrintMembersOfStaff: true,
    isPrintEventTimings: true,
    printFormat: REPORT_PRINT_FORMAT.PDF
  };

  return (
    <Formik
      onSubmit={values => {
        const {
          team,
          isPrintFullNames,
          isPrintPhotoConsentOptOut,
          isPrintDescription,
          isPrintKitNotes,
          isPrintComments,
          isPrintTransportDetails,
          isPrintOppositionContactDetails,
          isPrintMembersOfStaff,
          isPrintEventTimings,
          printFormat
        } = values;

        let isPrintXLSX = false;
        let isPrintPDF = false;

        switch (printFormat) {
          case REPORT_PRINT_FORMAT.XLSX:
            isPrintXLSX = true;
            break;
          case REPORT_PRINT_FORMAT.PDF:
            isPrintPDF = true;
            break;
        }

        let reportType = '';

        switch (true) {
          case isTeamSportEvent(event):
            reportType = REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.BASIC_TEAM_SHEET;

            break;

          case isIndividualSportEvent(event):
            reportType = REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.BASIC_TEAM_SHEET_INDIVIDUAL;

            break;
        }

        const data = {
          schoolId: activeSchoolId,
          eventId: eventId,
          teamId: team,
          isPrintFullNames,
          isPrintPhotoConsentOptOut,
          isPrintDescription,
          isPrintKitNotes,
          isPrintComments,
          isPrintTransportDetails,
          isPrintOppositionContactDetails,
          isPrintMembersOfStaff,
          isPrintEventTimings,
          isPrintXLSX,
          isPrintPDF
        };

        onSubmit(data, reportType);
      }}
      initialValues={requestInitial}
    >
      {({ values, setFieldValue }) => {
        const studentDetailsSectionNumber = isEmbeddedTeamsExist ? 2 : 1;
        const eventDetailsSectionNumber = studentDetailsSectionNumber + 1;
        const outputFormatSectionNumber = eventDetailsSectionNumber + 1;
        const renderToggle = (option: ToggleOption) => (
          <ReportToggle
            key={option.key}
            name={option.key}
            label={option.label}
            hint={option.hint}
            value={values[option.key]}
            onChange={value => setFieldValue(option.key, value)}
          />
        );

        return (
          <Form className="eBasicTeamSheetForm">
            <ReportDescription>
              Create a clear team sheet with the participant and event information you need. If the sheet will be
              displayed publicly, you can shorten student surnames below.
            </ReportDescription>

            {isEmbeddedTeamsExist && (
              <ReportSection number={1} title="Team" hint="Choose the team to include in this report.">
                <div className="eReportSelectWrap">
                  <select
                    id="basicTeamSheetTeam"
                    className="eReportSelect"
                    value={values.team}
                    onChange={event => setFieldValue('team', event.target.value)}
                  >
                    {teamOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m7 10 5 5 5-5" />
                  </svg>
                </div>
              </ReportSection>
            )}

            <ReportSection
              number={studentDetailsSectionNumber}
              title="Student details"
              hint="Choose which participant details should appear in the generated document."
            >
              <ReportToggleList>{studentDetailsOptions.map(renderToggle)}</ReportToggleList>
            </ReportSection>

            <ReportSection
              number={eventDetailsSectionNumber}
              title="Event details"
              hint="Choose which event details should be included with the team sheet."
            >
              <ReportToggleList>{eventDetailsOptions.map(renderToggle)}</ReportToggleList>
            </ReportSection>

            <ReportSection
              number={outputFormatSectionNumber}
              title="Output format"
              hint="Choose a printable PDF or an editable Excel file."
            >
              <ReportFormatSelector
                value={values.printFormat}
                pdfValue={REPORT_PRINT_FORMAT.PDF}
                xlsxValue={REPORT_PRINT_FORMAT.XLSX}
                onChange={value => setFieldValue('printFormat', value)}
              />
            </ReportSection>

            <ReportActions onCancel={onCloseClick} />
          </Form>
        );
      }}
    </Formik>
  );
}
